import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate, type Location } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useAuth } from "@/auth";
import { BrandMark, BrandName, Button } from "@/components/ui";
import { t } from "@/i18n";

interface LocationState {
  from?: Location;
}

/** Giris sayfasi — eski `legacy/login.html` gorunumu: marka, gradyan zemin, tek kart. */
export function Giris() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") {
    const state = location.state as LocationState | null;
    const from = state?.from?.pathname ?? "/ozet";
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate("/ozet", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-blue-pale to-bg px-4 py-8">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-[420px] space-y-4 rounded-2xl border-[1.5px] border-line bg-card p-7 shadow-login sm:p-9"
      >
        <div className="mb-2 flex flex-col items-center text-center">
          <BrandMark size={52} className="mb-3" />
          <BrandName className="text-[22px]" />
          <p className="mt-1 text-[13px] text-muted">{t("auth.login.tagline")}</p>
        </div>

        <h1 className="sr-only">{t("auth.login.title")}</h1>

        <div>
          <label htmlFor="email" className="label">
            {t("auth.login.email")}
          </label>
          <input id="email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
        </div>

        <div>
          <label htmlFor="password" className="label">
            {t("auth.login.password")}
          </label>
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
          />
        </div>

        {error && (
          <p role="alert" className="rounded-[9px] border-[1.5px] border-[#fecaca] bg-red-pale px-3 py-2 text-[13px] text-[#991b1b]">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" disabled={submitting} className="w-full !min-h-[46px] text-[15px]">
          {submitting ? t("auth.login.submitting") : t("auth.login.submit")}
        </Button>

        <div className="flex justify-between text-[13px]">
          <Link to="/kayit" className="font-medium text-brand-primary hover:underline">
            {t("auth.login.register_link")}
          </Link>
          <Link to="/sifre" className="font-medium text-brand-primary hover:underline">
            {t("auth.login.forgot_link")}
          </Link>
        </div>
      </form>
    </div>
  );
}
